import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import TopicSuggestions from "@/components/topic/TopicSuggestions";
import {
  Sparkles, Upload, Search, X, Trash2, Pencil, Download, Eye,
  ImageIcon, Loader2, Check, Wand2, Image as ImgIcon, PlusCircle,
  FileText, RefreshCw, ArrowRight, Layers, Package,
} from "lucide-react";

const TOKEN_KEY = "nx_token";
const getToken = () => localStorage.getItem(TOKEN_KEY) || "";

const FILTERS = [
  { key: "all",      label: { ar: "الكل",                 en: "All" } },
  { key: "uploaded", label: { ar: "مرفوعة",               en: "Uploaded" } },
  { key: "ai",       label: { ar: "مولدة بالذكاء الاصطناعي", en: "AI-generated" } },
  { key: "ready",    label: { ar: "جاهزة للنشر",          en: "Ready to publish" } },
];

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_MB = 10;

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
};

// Fetch image as blob so <img src> can carry auth. Cached in-component.
// Retries once with a short delay to survive the cold-storage window right
// after a fresh upload/generation completes.
function useAuthedImage(imageId) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const token = getToken();
    if (!imageId || !token) return;
    let revoked = null;
    let cancelled = false;
    const doFetch = async (attempt = 0) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/images/${imageId}/file`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          if (attempt < 2 && [404, 500, 502, 503].includes(res.status)) {
            setTimeout(() => { if (!cancelled) doFetch(attempt + 1); }, 1200 * (attempt + 1));
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const b = await res.blob();
        if (cancelled) return;
        const u = URL.createObjectURL(b);
        revoked = u;
        setUrl(u);
      } catch {
        if (!cancelled) setUrl(null);
      }
    };
    doFetch(0);
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [imageId]);
  return url;
}

function ImageThumb({ id, alt, className = "" }) {
  const url = useAuthedImage(id);
  if (!url) {
    return (
      <div data-testid={`image-thumb-loading-${id}`} className={`bg-muted/40 flex items-center justify-center ${className}`}>
        <ImageIcon className="w-6 h-6 text-muted-foreground/50" />
      </div>
    );
  }
  return <img data-testid={`image-thumb-${id}`} src={url} alt={alt || ""} className={className} loading="lazy" />;
}

// ------- Rename modal -------
function RenameModal({ open, image, lang, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (image) setName(image.name || ""); }, [image]);
  if (!open || !image) return null;
  const save = async () => {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      toast.error(lang === "ar" ? "الاسم مطلوب" : "Name is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/images/${image.id}`, { name: trimmed });
      toast.success(lang === "ar" ? "تم حفظ الاسم" : "Name saved");
      onSaved(data);
    } catch (e) {
      toast.error(lang === "ar" ? "تعذّر الحفظ" : "Failed to save");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border/60 rounded-md w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="image-rename-modal">
        <h3 className="text-lg font-semibold mb-3">{lang === "ar" ? "تعديل اسم الصورة" : "Rename image"}</h3>
        <input
          data-testid="image-rename-input"
          className="w-full px-3 py-2 rounded-sm border border-border/60 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          autoFocus
          maxLength={120}
        />
        <div className="mt-4 flex gap-2 justify-end">
          <button data-testid="image-rename-cancel" className="px-4 py-2 text-sm rounded-sm border border-border/60 hover:bg-muted/60" onClick={onClose}>
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button data-testid="image-rename-save" disabled={busy} className="px-4 py-2 text-sm rounded-sm bg-primary text-primary-foreground disabled:opacity-60 inline-flex items-center gap-2" onClick={save}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {lang === "ar" ? "حفظ" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------- Details modal -------
function DetailsModal({ open, image, lang, onClose, onDelete, onDownload, onEdit }) {
  const url = useAuthedImage(open && image ? image.id : null);
  if (!open || !image) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border/60 rounded-md w-full max-w-4xl max-h-[90vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="image-details-modal">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold">{image.name}</h3>
          <button data-testid="image-details-close" className="p-1 hover:bg-muted rounded-sm" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-[2fr_1fr] gap-5">
          <div className="bg-muted/30 rounded-md min-h-[300px] flex items-center justify-center overflow-hidden">
            {url ? <img src={url} alt={image.name} className="max-w-full max-h-[70vh] object-contain" /> : <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />}
          </div>
          <dl className="text-sm space-y-3">
            <div>
              <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الاسم" : "Name"}</dt>
              <dd className="font-medium break-all">{image.name}</dd>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الأبعاد" : "Dimensions"}</dt>
                <dd className="font-medium">{image.width && image.height ? `${image.width} × ${image.height}` : "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الحجم" : "Size"}</dt>
                <dd className="font-medium">{fmtBytes(image.file_size)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "النوع" : "Type"}</dt>
                <dd className="font-medium">{image.file_type || "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "المصدر" : "Source"}</dt>
                <dd className="font-medium">
                  {image.source === "ai" ? (lang === "ar" ? "مولدة بالذكاء الاصطناعي" : "AI-generated") : (lang === "ar" ? "مرفوعة" : "Uploaded")}
                </dd>
              </div>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">{lang === "ar" ? "تاريخ الإنشاء" : "Created"}</dt>
              <dd className="font-medium">{fmtDate(image.created_at, lang)}</dd>
            </div>
            {image.source === "ai" && (
              <>
                {image.aspect_ratio && (
                  <div>
                    <dt className="text-muted-foreground text-xs">{lang === "ar" ? "نسبة الأبعاد" : "Aspect ratio"}</dt>
                    <dd className="font-medium">{image.aspect_ratio}</dd>
                  </div>
                )}
                {image.style && image.style !== "auto" && image.style !== "edit" && (
                  <div>
                    <dt className="text-muted-foreground text-xs">{lang === "ar" ? "النمط" : "Style"}</dt>
                    <dd className="font-medium capitalize">{image.style.split(",").map((s) => s.trim()).filter(Boolean).join(" · ")}</dd>
                  </div>
                )}
                {image.prompt && (
                  <div>
                    <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الوصف الأصلي" : "Prompt"}</dt>
                    <dd className="font-medium text-xs leading-relaxed whitespace-pre-wrap bg-muted/40 p-2 rounded-sm mt-1" data-testid="image-details-prompt">{image.prompt}</dd>
                  </div>
                )}
              </>
            )}
            <div className="pt-3 flex flex-col gap-2">
              <button data-testid="image-details-download" onClick={() => onDownload(image)} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm">
                <Download className="w-4 h-4" /> {lang === "ar" ? "تحميل" : "Download"}
              </button>
              <button data-testid="image-details-edit" onClick={() => { onClose(); onEdit(image); }} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-primary/40 text-primary hover:bg-primary/10 text-sm">
                <Wand2 className="w-4 h-4" /> {lang === "ar" ? "تعديل بالذكاء الاصطناعي" : "Edit with AI"}
              </button>
              <button data-testid="image-details-delete" onClick={() => onDelete(image)} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-destructive/50 text-destructive hover:bg-destructive/10 text-sm">
                <Trash2 className="w-4 h-4" /> {lang === "ar" ? "حذف" : "Delete"}
              </button>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}

// ------- Info Design wizard (Content-to-Design pipeline) -------
const CONTENT_TYPE_OPTIONS = [
  { key: "auto",        labelAr: "اكتشاف تلقائي",   labelEn: "Auto detect" },
  { key: "visual",      labelAr: "صورة بصرية",       labelEn: "Visual only" },
  { key: "info",        labelAr: "معلومات ونصائح",   labelEn: "Info & tips" },
  { key: "infographic", labelAr: "إنفوجرافيك",       labelEn: "Infographic" },
  { key: "comparison",  labelAr: "مقارنة",           labelEn: "Comparison" },
  { key: "steps",       labelAr: "خطوات",            labelEn: "Steps" },
  { key: "facts",       labelAr: "حقائق سريعة",       labelEn: "Quick facts" },
  { key: "tips",        labelAr: "نصائح عملية",       labelEn: "Practical tips" },
];

function InfoDesignModal({ open, lang, onClose, onGenerated }) {
  // Wizard step: 1=brief, 2=preview/edit
  const [step, setStep] = useState(1);
  const [prompt, setPrompt] = useState("");
  const [contentType, setContentType] = useState("auto");
  const [trusted, setTrusted] = useState(false);
  const [aspect, setAspect] = useState("4:5");
  // Zero-Prompt UX (Sprint 27A): default to auto multi-slide so the user
  // never has to think about slide counts. DAAM decides.
  const [multi, setMulti] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [brainstorming, setBrainstorming] = useState(false);
  // Editable content
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [points, setPoints] = useState([]);
  const [closing, setClosing] = useState("");
  const [sources, setSources] = useState([]);
  const [detectedType, setDetectedType] = useState("info");
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1); setPrompt(""); setContentType("auto"); setTrusted(false);
      setAspect("4:5"); setMulti(true); setShowAdvanced(false);
      setBrainstorming(false); setRendering(false);
      setTitle(""); setSubtitle(""); setPoints([]); setClosing(""); setSources([]);
      setDetectedType("info");
    }
  }, [open]);

  const applyContent = (data) => {
    const c = data.content || {};
    setDetectedType(data.content_type || "info");
    setTitle(c.title || "");
    setSubtitle(c.subtitle || "");
    setPoints(c.points || []);
    setClosing(c.closing || "");
    setSources(c.sources || []);
  };

  const brainstorm = async () => {
    const p = prompt.trim();
    if (p.length < 3) {
      toast.error(lang === "ar" ? "الموضوع قصير جداً" : "Topic is too short");
      return;
    }
    setBrainstorming(true);
    try {
      if (multi) {
        // Multi-slide path: skip the editable preview step. Claude designs
        // the whole series and we submit for rendering immediately.
        // Idempotency key is generated once per submit — protects against
        // rapid double-clicks / network retries creating duplicate jobs.
        const idempotencyKey = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const { data } = await api.post("/images/content/multi", {
          prompt: p, aspect_ratio: aspect, trusted, styles: "",
          idempotency_key: idempotencyKey,
        });
        // Server may echo `status: "resumed"` or `"deduplicated"` when the
        // idempotency layer returned an existing set. Use it to tune copy.
        const t = data.status === "resumed" || data.status === "deduplicated"
          ? (lang === "ar" ? "استئناف الجلسة السابقة" : "Resumed previous session")
          : (lang === "ar" ? "دَعم يخطط سلسلتك…" : "DAAM is planning your series…");
        toast.success(t);
        (data.slides || []).forEach((s) => onGenerated(s));
        onClose();
      } else {
        const { data } = await api.post("/images/content/brainstorm", {
          prompt: p, content_type: contentType, trusted,
        });
        applyContent(data);
        setStep(2);
        toast.success(lang === "ar" ? "تم توليد المحتوى، عدّل ما تريد ثم اعتمد" : "Content ready — edit and approve");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message_ar || (lang === "ar" ? "تعذّر توليد المحتوى" : "Content generation failed"));
    } finally { setBrainstorming(false); }
  };

  const rewriteContent = async () => {
    setBrainstorming(true);
    try {
      const { data } = await api.post("/images/content/brainstorm", {
        prompt, content_type: detectedType, trusted,
      });
      applyContent(data);
      toast.success(lang === "ar" ? "تم إعادة كتابة المحتوى" : "Content rewritten");
    } catch {
      toast.error(lang === "ar" ? "تعذّر إعادة الكتابة" : "Rewrite failed");
    } finally { setBrainstorming(false); }
  };

  const approve = async () => {
    if (!title.trim()) {
      toast.error(lang === "ar" ? "العنوان مطلوب" : "Title is required");
      return;
    }
    setRendering(true);
    try {
      const { data } = await api.post("/images/content/render", {
        prompt, content_type: detectedType,
        aspect_ratio: aspect,
        content: {
          title: title.trim(),
          subtitle: subtitle.trim(),
          points: points.map((p) => p.trim()).filter(Boolean),
          closing: closing.trim(),
          sources,
        },
      });
      toast.success(lang === "ar" ? "دَعم يبدأ العمل على التصميم…" : "DAAM is starting on your design…");
      onGenerated(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message_ar || (lang === "ar" ? "تعذّر إنشاء التصميم" : "Design failed"));
    } finally { setRendering(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-card border border-border/60 rounded-md w-full max-w-2xl p-5 my-8" onClick={(e) => e.stopPropagation()} data-testid="info-design-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold inline-flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {lang === "ar" ? "إنشاء تصميم معلوماتي" : "Create Informational Design"}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {step === 1
                ? (lang === "ar" ? "الخطوة 1 من 2 — اكتب الموضوع وسنولّد المحتوى" : "Step 1 of 2 — describe the topic, we'll generate content")
                : (lang === "ar" ? "الخطوة 2 من 2 — راجع وعدّل ثم اعتمد التصميم" : "Step 2 of 2 — review, edit, then approve the design")}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="info-design-close"><X className="w-5 h-5" /></button>
        </div>

        {step === 1 ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">
                {lang === "ar" ? "الموضوع" : "Topic"}
              </label>
              <textarea
                data-testid="info-design-prompt"
                rows={3} maxLength={2000}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={lang === "ar"
                  ? "مثال: أفضل الأماكن في شمال إيطاليا"
                  : "e.g. Best places in northern Italy"}
                className="w-full px-3 py-2 rounded-sm border border-border/60 bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                dir="auto"
              />
              <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                {lang === "ar"
                  ? "اكتب موضوعاً فقط. دَعم يختار عدد الشرائح، الأسلوب البصري، والألوان تلقائياً."
                  : "Just type a topic. DAAM picks the slide count, visual style, and colour palette automatically."}
              </p>
            </div>

            <TopicSuggestions
              lang={lang}
              onPick={(t) => setPrompt(t)}
              testIdPrefix="info-design-suggestions"
            />

            <div className="pt-1">
              <button
                type="button"
                data-testid="info-design-advanced-toggle"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <ArrowRight className={`w-3 h-3 transition-transform ${showAdvanced ? "rotate-90" : ""}`} />
                {lang === "ar" ? "خيارات متقدمة" : "Advanced options"}
              </button>
              {showAdvanced && (
                <div className="mt-2 p-3 rounded-sm bg-muted/30 border border-border/40 space-y-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">
                {lang === "ar" ? "نوع المحتوى" : "Content type"}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                {CONTENT_TYPE_OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    data-testid={`info-design-ctype-${o.key}`}
                    onClick={() => setContentType(o.key)}
                    className={`px-3 py-2 text-xs rounded-sm border transition-colors ${
                      contentType === o.key
                        ? "bg-primary/10 text-primary border-primary/40"
                        : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    }`}
                  >{lang === "ar" ? o.labelAr : o.labelEn}</button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">{lang === "ar" ? "نسبة الأبعاد" : "Aspect ratio"}</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {ASPECT_OPTIONS.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      data-testid={`info-design-aspect-${o.key.replace(":","x")}`}
                      onClick={() => setAspect(o.key)}
                      className={`px-2 py-2 text-[11px] rounded-sm border transition-colors ${
                        aspect === o.key ? "bg-primary/10 text-primary border-primary/40" : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60"
                      }`}
                    >{o.key}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">{lang === "ar" ? "دقة المحتوى" : "Accuracy"}</label>
                <button
                  type="button"
                  data-testid="info-design-trusted"
                  onClick={() => setTrusted(!trusted)}
                  className={`w-full inline-flex items-center justify-between px-3 py-2 rounded-sm border text-xs transition-colors ${
                    trusted ? "bg-primary/10 text-primary border-primary/40" : "border-border/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <span>{lang === "ar" ? "محتوى أكثر دقة" : "Enhanced accuracy"}</span>
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center ${trusted ? "bg-primary text-primary-foreground" : "border border-border"}`}>{trusted && <Check className="w-3 h-3" />}</span>
                </button>
                <p data-testid="info-design-trusted-disclaimer" className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                  {lang === "ar"
                    ? "يعتمد على تحسين المحتوى بالذكاء الاصطناعي ولا يتضمن بحثًا مباشرًا من الإنترنت حاليًا."
                    : "Uses AI content refinement and does not currently perform live web research."}
                </p>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">{lang === "ar" ? "عدد الشرائح" : "Number of slides"}</label>
              <button
                type="button"
                data-testid="info-design-multi"
                onClick={() => setMulti(!multi)}
                className={`w-full inline-flex items-center justify-between px-3 py-2 rounded-sm border text-xs transition-colors ${
                  multi ? "bg-primary/10 text-primary border-primary/40" : "border-border/60 text-muted-foreground hover:text-foreground"
                }`}
              >
                <span>{multi
                  ? (lang === "ar" ? "تلقائي — سلسلة شرائح متكاملة (موصى به)" : "Auto — full slide series (recommended)")
                  : (lang === "ar" ? "شريحة واحدة فقط" : "Single slide only")}</span>
                <span className={`w-4 h-4 rounded-full flex items-center justify-center ${multi ? "bg-primary text-primary-foreground" : "border border-border"}`}>{multi && <Check className="w-3 h-3" />}</span>
              </button>
              <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                {lang === "ar"
                  ? "يقرّر دَعم عدد الشرائح بناءً على غنى الموضوع"
                  : "DAAM decides how many slides your topic needs"}
              </p>
            </div>
                </div>
              )}
            </div>
            <div className="pt-2 flex gap-2 justify-end">
              <button data-testid="info-design-step1-cancel" onClick={onClose} className="px-4 py-2 text-sm rounded-sm border border-border/60 hover:bg-muted/60">
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button
                data-testid="info-design-brainstorm"
                disabled={brainstorming || prompt.trim().length < 3}
                onClick={brainstorm}
                className="px-5 py-2 text-sm rounded-sm bg-primary text-primary-foreground inline-flex items-center gap-2 disabled:opacity-60 font-medium"
              >
                {brainstorming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {brainstorming
                  ? (lang === "ar" ? "دَعم يفكر…" : "DAAM is thinking…")
                  : (lang === "ar" ? "إنشاء التصميم" : "Create design")}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-[11px] px-2 py-1 rounded-sm bg-primary/10 text-primary inline-block">
              {lang === "ar" ? `نوع المحتوى: ${(CONTENT_TYPE_OPTIONS.find(x => x.key === detectedType)?.labelAr) || detectedType}` : `Type: ${detectedType}`}
              {trusted && <span className="ms-2">· {lang === "ar" ? "محتوى أكثر دقة" : "Enhanced accuracy"}</span>}
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">{lang === "ar" ? "العنوان الرئيسي" : "Main title"}</label>
              <input
                data-testid="info-design-edit-title"
                dir="auto"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                className="w-full px-3 py-2 rounded-sm border border-border/60 bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">{lang === "ar" ? "العنوان الفرعي" : "Subtitle"}</label>
              <input
                data-testid="info-design-edit-subtitle"
                dir="auto"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                maxLength={200}
                className="w-full px-3 py-2 rounded-sm border border-border/60 bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">{lang === "ar" ? "النقاط" : "Points"}</label>
              <div className="space-y-1.5">
                {points.map((p, i) => (
                  <div key={i} className="flex gap-1.5">
                    <textarea
                      data-testid={`info-design-edit-point-${i}`}
                      dir="auto"
                      rows={2}
                      value={p}
                      onChange={(e) => setPoints(points.map((v, idx) => idx === i ? e.target.value : v))}
                      className="flex-1 px-3 py-2 rounded-sm border border-border/60 bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                    />
                    <button
                      data-testid={`info-design-remove-point-${i}`}
                      type="button"
                      onClick={() => setPoints(points.filter((_, idx) => idx !== i))}
                      className="p-2 rounded-sm border border-border/60 text-muted-foreground hover:text-destructive"
                    ><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {points.length < 8 && (
                  <button
                    type="button"
                    data-testid="info-design-add-point"
                    onClick={() => setPoints([...points, ""])}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                  ><PlusCircle className="w-3 h-3" /> {lang === "ar" ? "إضافة نقطة" : "Add point"}</button>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">{lang === "ar" ? "خاتمة (اختياري)" : "Closing note (optional)"}</label>
              <input
                data-testid="info-design-edit-closing"
                dir="auto"
                value={closing}
                onChange={(e) => setClosing(e.target.value)}
                maxLength={300}
                className="w-full px-3 py-2 rounded-sm border border-border/60 bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            {sources.length > 0 && (
              <div className="text-[11px] text-muted-foreground">
                {lang === "ar" ? "مصادر" : "Sources"}: {sources.join(", ")}
              </div>
            )}
            <div className="pt-2 flex gap-2 justify-between flex-wrap">
              <button
                data-testid="info-design-back"
                onClick={() => setStep(1)}
                className="px-4 py-2 text-sm rounded-sm border border-border/60 hover:bg-muted/60"
              >{lang === "ar" ? "رجوع" : "Back"}</button>
              <div className="flex gap-2">
                <button
                  data-testid="info-design-rewrite"
                  disabled={brainstorming}
                  onClick={rewriteContent}
                  className="px-4 py-2 text-sm rounded-sm border border-border/60 hover:bg-muted/60 inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {brainstorming ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {lang === "ar" ? "إعادة كتابة المحتوى" : "Rewrite"}
                </button>
                <button
                  data-testid="info-design-approve"
                  disabled={rendering || !title.trim()}
                  onClick={approve}
                  className="px-4 py-2 text-sm rounded-sm bg-primary text-primary-foreground inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {lang === "ar" ? "اعتماد وإنشاء التصميم" : "Approve & generate"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ------- AI Generate modal -------
const ASPECT_OPTIONS = [
  { key: "1:1",  labelAr: "مربع (1:1)",          labelEn: "Square (1:1)" },
  { key: "9:16", labelAr: "عمودي (9:16)",         labelEn: "Portrait (9:16)" },
  { key: "16:9", labelAr: "أفقي (16:9)",          labelEn: "Landscape (16:9)" },
  { key: "4:5",  labelAr: "إنستجرام (4:5)",       labelEn: "Instagram (4:5)" },
];
const STYLE_OPTIONS = [
  { key: "auto",       labelAr: "تلقائي",   labelEn: "Auto" },
  { key: "realistic",  labelAr: "واقعي",    labelEn: "Realistic" },
  { key: "modern",     labelAr: "عصري",     labelEn: "Modern" },
  { key: "cinematic",  labelAr: "سينمائي",  labelEn: "Cinematic" },
  { key: "minimal",    labelAr: "بسيط",     labelEn: "Minimal" },
  { key: "luxury",     labelAr: "فاخر",     labelEn: "Luxury" },
];

// Small helper: preview thumb strip for a list of File objects.
function FilePreviewStrip({ files, onRemove, testIdPrefix }) {
  if (!files || !files.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {files.map((f, i) => (
        <div key={i} className="relative w-16 h-16 rounded-sm overflow-hidden border border-border/60 bg-muted/30 group">
          <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" onLoad={(e) => URL.revokeObjectURL(e.target.src)} />
          <button
            type="button"
            data-testid={`${testIdPrefix}-remove-${i}`}
            onClick={() => onRemove(i)}
            className="absolute top-0.5 end-0.5 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          ><X className="w-3 h-3" /></button>
        </div>
      ))}
    </div>
  );
}

function AIGenerateModal({ open, lang, onClose, onGenerated }) {
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("1:1");
  const [styles, setStyles] = useState([]); // empty = Best style automatically
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [refs, setRefs] = useState([]); // File[]
  const [logo, setLogo] = useState(null); // File
  const [busy, setBusy] = useState(false);
  const refsInput = useRef(null);
  const logoInput = useRef(null);

  useEffect(() => {
    if (open) {
      setPrompt(""); setAspect("1:1"); setStyles([]); setShowAdvanced(false);
      setRefs([]); setLogo(null);
    }
  }, [open]);
  if (!open) return null;

  const toggleStyle = (key) => {
    setStyles((prev) => {
      if (key === "auto") return [];
      return prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key];
    });
  };
  const addRefs = (files) => {
    const arr = Array.from(files || []).filter((f) => f && f.size <= 10 * 1024 * 1024);
    setRefs((prev) => [...prev, ...arr].slice(0, 4));
  };
  const removeRef = (idx) => setRefs((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    const p = prompt.trim();
    if (p.length < 3) {
      toast.error(lang === "ar" ? "الموضوع قصير جداً" : "Topic is too short");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("prompt", p);
      form.append("aspect_ratio", aspect);
      // Empty styles = auto (Claude-directed). Only send explicit styles when user picked some.
      form.append("styles", styles.length ? styles.join(",") : "auto");
      refs.forEach((f) => form.append("reference_files", f));
      if (logo) form.append("logo_file", logo);
      const { data } = await api.post("/images/generate", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(lang === "ar" ? "دَعم يبدأ العمل على الصورة…" : "DAAM is starting on your image…");
      onGenerated(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = detail?.message_ar || detail?.error || (lang === "ar" ? "تعذّر إنشاء الصورة" : "Generation failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-card border border-border/60 rounded-md w-full max-w-lg p-5 my-8" onClick={(e) => e.stopPropagation()} data-testid="ai-generate-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold inline-flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              {lang === "ar" ? "إنشاء صورة بالذكاء الاصطناعي" : "Generate with AI"}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {lang === "ar" ? "اكتب موضوعاً — دَعم يختار الأسلوب البصري ويكوّن الصورة" : "Type a topic — DAAM picks the visual style and composes the image"}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="ai-generate-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">
              {lang === "ar" ? "الموضوع" : "Topic"}
            </label>
            <textarea
              data-testid="ai-generate-prompt"
              rows={4}
              maxLength={2000}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={lang === "ar"
                ? "مثال: منتج عطر فاخر على رخام أسود"
                : "e.g. Luxury perfume bottle on black marble"}
              className="w-full px-3 py-2 rounded-sm border border-border/60 bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              dir="auto"
            />
            <div className="text-[10px] text-muted-foreground mt-1 text-end">{prompt.length}/2000</div>
          </div>
          <TopicSuggestions
            lang={lang}
            onPick={(t) => setPrompt(t)}
            testIdPrefix="ai-generate-suggestions"
          />
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">
              {lang === "ar" ? "نسبة الأبعاد" : "Aspect ratio"}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
              {ASPECT_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  data-testid={`ai-generate-aspect-${o.key.replace(":", "x")}`}
                  onClick={() => setAspect(o.key)}
                  className={`px-3 py-2 text-xs rounded-sm border transition-colors ${
                    aspect === o.key
                      ? "bg-primary/10 text-primary border-primary/40"
                      : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60"
                  }`}
                >{lang === "ar" ? o.labelAr : o.labelEn}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground block mb-1.5">
              {lang === "ar" ? "أسلوب التصميم" : "Visual style"}
            </div>
            <div
              data-testid="ai-generate-style-auto"
              className={`px-4 py-3 rounded-sm border cursor-pointer transition-colors ${
                styles.length === 0
                  ? "bg-primary/10 border-primary/40"
                  : "border-border/60 hover:bg-muted/60"
              }`}
              onClick={() => setStyles([])}
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkles className={`w-4 h-4 ${styles.length === 0 ? "text-primary" : "text-muted-foreground"}`} />
                <span className={styles.length === 0 ? "text-primary" : "text-foreground"}>
                  {lang === "ar" ? "أفضل أسلوب تلقائيًا" : "Best style automatically"}
                </span>
                {styles.length === 0 && <Check className="w-3.5 h-3.5 text-primary ms-auto" />}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {lang === "ar"
                  ? "يختار الذكاء الاصطناعي أفضل أسلوب بناءً على وصفك"
                  : "AI selects the most suitable visual style based on your description"}
              </p>
            </div>
            <button
              type="button"
              data-testid="ai-generate-advanced-toggle"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="mt-2 text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <ArrowRight className={`w-3 h-3 transition-transform ${showAdvanced ? "rotate-90" : ""}`} />
              {lang === "ar" ? "خيارات متقدمة" : "Advanced options"}
            </button>
            {showAdvanced && (
              <div className="mt-2 p-3 rounded-sm bg-muted/30 border border-border/40">
                <div className="text-[11px] text-muted-foreground mb-1.5">
                  {lang === "ar" ? "اختر أسلوباً يدوياً (يمكن دمج أكثر من نمط)" : "Choose a manual style (combine multiple)"}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {STYLE_OPTIONS.filter((o) => o.key !== "auto").map((o) => {
                    const active = styles.includes(o.key);
                    return (
                      <button
                        key={o.key}
                        type="button"
                        data-testid={`ai-generate-style-${o.key}`}
                        onClick={() => toggleStyle(o.key)}
                        className={`px-3 py-1.5 text-xs rounded-sm border transition-colors inline-flex items-center gap-1 ${
                          active
                            ? "bg-primary/10 text-primary border-primary/40"
                            : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60"
                        }`}
                      >
                        {active && <Check className="w-3 h-3" />}
                        {lang === "ar" ? o.labelAr : o.labelEn}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">
                {lang === "ar" ? "صور مرجعية (اختياري، حتى 4)" : "Reference images (optional, up to 4)"}
              </label>
              <button
                type="button"
                data-testid="ai-generate-refs-add"
                onClick={() => refsInput.current?.click()}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-dashed border-border/60 hover:bg-muted/60 text-xs text-muted-foreground"
              >
                <ImgIcon className="w-4 h-4" />
                {lang === "ar" ? "إضافة مراجع" : "Add references"}
              </button>
              <input ref={refsInput} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => addRefs(e.target.files)} data-testid="ai-generate-refs-input" />
              <FilePreviewStrip files={refs} onRemove={removeRef} testIdPrefix="ai-generate-ref" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">
                {lang === "ar" ? "شعار (اختياري)" : "Logo (optional)"}
              </label>
              <button
                type="button"
                data-testid="ai-generate-logo-add"
                onClick={() => logoInput.current?.click()}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-dashed border-border/60 hover:bg-muted/60 text-xs text-muted-foreground"
              >
                <PlusCircle className="w-4 h-4" />
                {logo ? (lang === "ar" ? "استبدال الشعار" : "Replace logo") : (lang === "ar" ? "إضافة شعار" : "Add logo")}
              </button>
              <input ref={logoInput} type="file" accept={ACCEPT} className="hidden" onChange={(e) => setLogo(e.target.files?.[0] || null)} data-testid="ai-generate-logo-input" />
              {logo && (
                <div className="relative w-16 h-16 rounded-sm overflow-hidden border border-border/60 bg-muted/30 group mt-2">
                  <img src={URL.createObjectURL(logo)} alt="logo" className="w-full h-full object-contain" onLoad={(e) => URL.revokeObjectURL(e.target.src)} />
                  <button type="button" data-testid="ai-generate-logo-remove" onClick={() => setLogo(null)} className="absolute top-0.5 end-0.5 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          </div>
          <div className="pt-2 flex gap-2 justify-end">
            <button data-testid="ai-generate-cancel" onClick={onClose} className="px-4 py-2 text-sm rounded-sm border border-border/60 hover:bg-muted/60">
              {lang === "ar" ? "إلغاء" : "Cancel"}
            </button>
            <button
              data-testid="ai-generate-submit"
              disabled={busy || prompt.trim().length < 3}
              onClick={submit}
              className="px-4 py-2 text-sm rounded-sm bg-primary text-primary-foreground inline-flex items-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {lang === "ar" ? "إنشاء الصورة" : "Generate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------- AI Edit modal (any image → new AI edit) -------
function AIEditModal({ open, image, lang, onClose, onEdited }) {
  const [prompt, setPrompt] = useState("");
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const refsInput = useRef(null);
  useEffect(() => { if (open) { setPrompt(""); setRefs([]); } }, [open]);
  const url = useAuthedImage(open && image ? image.id : null);
  if (!open || !image) return null;
  const submit = async () => {
    const p = prompt.trim();
    if (p.length < 3) {
      toast.error(lang === "ar" ? "تعليمات التعديل قصيرة جداً" : "Edit instruction is too short");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("prompt", p);
      refs.forEach((f) => form.append("reference_files", f));
      const { data } = await api.post(`/images/${image.id}/edit`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(lang === "ar" ? "بدأ تعديل الصورة…" : "Edit started…");
      onEdited(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = detail?.message_ar || detail?.error || (lang === "ar" ? "تعذّر التعديل" : "Edit failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };
  const suggestions = lang === "ar" ? [
    "أضف الشعار في الزاوية العلوية اليمنى",
    "استبدل الخلفية بمشهد مدينة عصرية",
    "حسّن الإضاءة واجعل الصورة أكثر فخامة",
    "أضف بوكيه ذهبي في الخلفية",
    "أزل النص الموجود في الصورة",
  ] : [
    "Add the logo in the top-right corner",
    "Replace background with a modern cityscape",
    "Improve lighting and make it more premium",
    "Add warm golden bokeh in the background",
    "Remove the existing text in the image",
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-card border border-border/60 rounded-md w-full max-w-lg p-5 my-8" onClick={(e) => e.stopPropagation()} data-testid="ai-edit-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold inline-flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-primary" />
              {lang === "ar" ? "تعديل بالذكاء الاصطناعي" : "Edit with AI"}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {lang === "ar" ? "سيتم إنشاء نسخة جديدة، لن يتم استبدال الأصل" : "A new copy will be created — the original is preserved"}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="ai-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div className="flex gap-3 items-start bg-muted/30 rounded-sm p-2">
            <div className="w-20 h-20 rounded-sm overflow-hidden bg-muted/60 shrink-0">
              {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <Loader2 className="w-5 h-5 m-auto animate-spin text-muted-foreground" />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{image.name}</div>
              <div className="text-xs text-muted-foreground">
                {image.width && image.height ? `${image.width}×${image.height}` : ""}
                {image.aspect_ratio ? ` · ${image.aspect_ratio}` : ""}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">
              {lang === "ar" ? "تعليمات التعديل" : "Edit instruction"}
            </label>
            <textarea
              data-testid="ai-edit-prompt"
              rows={4}
              maxLength={2000}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={lang === "ar" ? "اكتب ما تريد تغييره…" : "Describe what to change…"}
              className="w-full px-3 py-2 rounded-sm border border-border/60 bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`ai-edit-suggestion-${i}`}
                  onClick={() => setPrompt(s)}
                  className="px-2 py-1 text-[11px] rounded-sm border border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60"
                >{s}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">
              {lang === "ar" ? "مراجع إضافية (اختياري)" : "Extra references (optional)"}
            </label>
            <button
              type="button"
              data-testid="ai-edit-refs-add"
              onClick={() => refsInput.current?.click()}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-dashed border-border/60 hover:bg-muted/60 text-xs text-muted-foreground"
            >
              <ImgIcon className="w-4 h-4" />
              {lang === "ar" ? "إضافة مراجع" : "Add references"}
            </button>
            <input ref={refsInput} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { const arr = Array.from(e.target.files || []).filter((f) => f.size <= 10*1024*1024); setRefs((prev) => [...prev, ...arr].slice(0,4)); }} data-testid="ai-edit-refs-input" />
            <FilePreviewStrip files={refs} onRemove={(i) => setRefs((prev) => prev.filter((_, idx) => idx !== i))} testIdPrefix="ai-edit-ref" />
          </div>
          <div className="pt-2 flex gap-2 justify-end">
            <button data-testid="ai-edit-cancel" onClick={onClose} className="px-4 py-2 text-sm rounded-sm border border-border/60 hover:bg-muted/60">
              {lang === "ar" ? "إلغاء" : "Cancel"}
            </button>
            <button
              data-testid="ai-edit-submit"
              disabled={busy || prompt.trim().length < 3}
              onClick={submit}
              className="px-4 py-2 text-sm rounded-sm bg-primary text-primary-foreground inline-flex items-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {lang === "ar" ? "تعديل" : "Edit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// Rotating human-friendly copy shown on generating image cards. Cycles
// through 4 messages every 2.4s so the user feels the studio "at work"
// even though the backend only exposes a single `generating` status today.
const _GEN_COPY = {
  ar: [
    "دَعم يختار الأسلوب البصري…",
    "توليف اللوحة اللونية…",
    "رسم التركيبة المثالية…",
    "لمسة أخيرة قبل التقديم…",
  ],
  en: [
    "DAAM is choosing the visual style…",
    "Mixing the colour palette…",
    "Composing the ideal frame…",
    "Adding the final polish…",
  ],
};
function RotatingGeneratingCopy({ lang }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % 4), 2400);
    return () => clearInterval(t);
  }, []);
  const copy = (_GEN_COPY[lang] || _GEN_COPY.en)[i];
  return (
    <span className="text-[11px] leading-snug text-foreground/80 relative z-10 px-3 text-center transition-opacity">
      {copy}
    </span>
  );
}

// ------- Image card -------
function ImageCard({ img, lang, onOpen, onRename, onDelete, onDownload, onEdit, onRetry }) {
  const isGenerating = img.status === "generating";
  const isPlanning   = img.status === "planning";
  const isFailed     = img.status === "failed";
  const disabled     = isGenerating || isFailed || isPlanning;
  return (
    <div data-testid={`image-card-${img.id}`} className="group border border-border/60 rounded-md bg-card overflow-hidden hover:border-primary/40 transition-all">
      <div className={`relative aspect-square bg-muted/30 overflow-hidden ${disabled ? "cursor-default" : "cursor-pointer"}`} onClick={() => { if (!disabled) onOpen(img); }}>
        {isPlanning ? (
          <div data-testid={`image-planning-${img.id}`} className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground bg-primary/[0.04]">
            <div className="absolute inset-0 overflow-hidden opacity-50 pointer-events-none">
              <div className="absolute inset-y-0 -inset-x-1/2 w-1/2 bg-gradient-to-r from-transparent via-primary/30 to-transparent shimmer-strip" />
            </div>
            <Sparkles className="w-8 h-8 text-primary relative z-10 animate-pulse" />
            <span className="text-[11px] font-medium text-foreground/80 leading-snug relative z-10 px-3 text-center" dir="auto">
              {lang === "ar" ? "دَعم يخطط سلسلتك…" : "DAAM is planning your series…"}
            </span>
            <span className="text-[10px] text-muted-foreground/80 relative z-10">
              {lang === "ar" ? "الشرائح ستظهر تلقائياً" : "Slides will appear here"}
            </span>
          </div>
        ) : isGenerating ? (
          <div data-testid={`image-generating-${img.id}`} className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground bg-muted/40">
            {/* Subtle shimmer strip */}
            <div className="absolute inset-0 overflow-hidden opacity-40 pointer-events-none">
              <div className="absolute inset-y-0 -inset-x-1/2 w-1/2 bg-gradient-to-r from-transparent via-primary/25 to-transparent shimmer-strip" />
            </div>
            <Loader2 className="w-8 h-8 animate-spin text-primary relative z-10" />
            <RotatingGeneratingCopy lang={lang} />
          </div>
        ) : isFailed ? (
          <div data-testid={`image-failed-${img.id}`} className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
            <div className="w-10 h-10 rounded-sm bg-destructive/10 flex items-center justify-center">
              <X className="w-5 h-5 text-destructive" />
            </div>
            <span className="text-xs text-destructive font-medium">{lang === "ar" ? "فشل الإنشاء" : "Failed"}</span>
            {img.error_message && (
              <span className="text-[10px] text-muted-foreground/80 leading-tight line-clamp-3 max-w-full px-2">
                {img.error_message}
              </span>
            )}
            {/* Retry only for truly transient errors — never for budget /
                moderation / permanent failures, which won't succeed on retry
                and would just waste time. */}
            {img.slide_total > 1
              && !["budget_exceeded", "moderation_blocked"].includes(img.error_reason)
              && (
              <button
                type="button"
                data-testid={`image-retry-${img.id}`}
                onClick={(e) => { e.stopPropagation(); onRetry?.(img); }}
                className="mt-1 inline-flex items-center gap-1 text-[11px] px-3 py-1 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <RefreshCw className="w-3 h-3" />
                {lang === "ar" ? "إعادة المحاولة" : "Retry"}
              </button>
            )}
          </div>
        ) : (
          <ImageThumb key={`${img.id}-${img.status}`} id={img.id} alt={img.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        )}
        <span
          data-testid={`image-source-badge-${img.id}`}
          className={`absolute top-2 start-2 text-[10px] px-2 py-0.5 rounded-sm backdrop-blur-sm ${
            img.source === "ai" ? "bg-primary/80 text-primary-foreground" : "bg-background/80 text-foreground"
          }`}
        >
          {img.source === "ai"
            ? (lang === "ar" ? "مولدة بالذكاء الاصطناعي" : "AI")
            : (lang === "ar" ? "مرفوعة" : "Uploaded")}
        </span>
        {img.slide_total && img.slide_total > 1 && (
          <span
            data-testid={`image-slide-badge-${img.id}`}
            className="absolute top-2 end-2 text-[10px] px-2 py-0.5 rounded-sm bg-black/70 text-white backdrop-blur-sm inline-flex items-center gap-1"
          >
            <Layers className="w-3 h-3" />
            {(img.slide_index ?? 0) + 1}/{img.slide_total}
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="text-sm font-medium truncate" title={img.name}>{img.name}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(img.created_at, lang)}</div>
        <div className="mt-3 flex items-center gap-1">
          <button data-testid={`image-action-view-${img.id}`} disabled={disabled} title={lang === "ar" ? "عرض" : "View"} onClick={() => onOpen(img)} className="p-1.5 rounded-sm hover:bg-muted/60 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"><Eye className="w-4 h-4" /></button>
          <button data-testid={`image-action-edit-${img.id}`} disabled={disabled} title={lang === "ar" ? "تعديل بالذكاء الاصطناعي" : "Edit with AI"} onClick={() => onEdit(img)} className="p-1.5 rounded-sm hover:bg-muted/60 text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"><Wand2 className="w-4 h-4" /></button>
          <button data-testid={`image-action-rename-${img.id}`} disabled={isGenerating} title={lang === "ar" ? "تعديل الاسم" : "Rename"} onClick={() => onRename(img)} className="p-1.5 rounded-sm hover:bg-muted/60 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"><Pencil className="w-4 h-4" /></button>
          <button data-testid={`image-action-download-${img.id}`} disabled={disabled} title={lang === "ar" ? "تحميل" : "Download"} onClick={() => onDownload(img)} className="p-1.5 rounded-sm hover:bg-muted/60 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"><Download className="w-4 h-4" /></button>
          <button data-testid={`image-action-delete-${img.id}`} title={lang === "ar" ? "حذف" : "Delete"} onClick={() => onDelete(img)} className="p-1.5 rounded-sm hover:bg-muted/60 text-muted-foreground hover:text-destructive ms-auto"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

// ------- Slide-set group (multi-slide series) -------
function SlideSetGroup({ setId, setTitle, slides, lang, downloading, onDownloadAll, onOpen, onRename, onDelete, onDownload, onEdit, onRetry }) {
  const total = slides.length;
  const anyGenerating = slides.some((s) => s.status === "generating");
  const readyCount = slides.filter((s) => s.status === "ready").length;
  return (
    <section
      data-testid={`slide-set-${setId}`}
      className="border border-primary/30 rounded-md bg-primary/5 p-4 mb-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-sm bg-primary/15 flex items-center justify-center shrink-0">
            <Layers className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 data-testid={`slide-set-title-${setId}`} className="text-sm font-semibold truncate" title={setTitle}>
              {setTitle || (lang === "ar" ? "سلسلة شرائح" : "Slide series")}
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {lang === "ar"
                ? `${total} شرائح · ${readyCount} جاهزة${anyGenerating ? " · جاري الإنشاء…" : ""}`
                : `${total} slides · ${readyCount} ready${anyGenerating ? " · generating…" : ""}`}
            </p>
          </div>
        </div>
        <button
          data-testid={`slide-set-download-all-${setId}`}
          disabled={readyCount === 0 || downloading}
          onClick={() => onDownloadAll(setId, setTitle)}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          title={lang === "ar" ? "تحميل الشرائح مضغوطة" : "Download all as ZIP"}
        >
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
          {lang === "ar" ? "تحميل الكل (ZIP)" : "Download all (ZIP)"}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {slides.map((img) => (
          <ImageCard
            key={img.id}
            img={img}
            lang={lang}
            onOpen={onOpen}
            onRename={onRename}
            onDelete={onDelete}
            onDownload={onDownload}
            onEdit={onEdit}
            onRetry={onRetry}
          />
        ))}
      </div>
    </section>
  );
}

// ------- Main page -------
export default function ImagesPage() {
  const { lang } = useI18n();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [aiModal, setAiModal] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [downloadingSet, setDownloadingSet] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter && filter !== "all") params.set("source", filter);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/images?${params.toString()}`);
      setImages(data.images || []);
    } catch {
      toast.error(lang === "ar" ? "تعذّر تحميل الصور" : "Failed to load images");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Poll for status of any generating images every 3s until all resolve.
  // Also refresh the full list when a `planning` placeholder is present, so
  // the real slides picked up in the background replace the shimmer card.
  const generatingIds = images.filter((i) => i.status === "generating").map((i) => i.id).join(",");
  const hasPlanning   = images.some((i) => i.status === "planning");
  useEffect(() => {
    if (!generatingIds && !hasPlanning) return;
    const ids = generatingIds ? generatingIds.split(",") : [];
    const t = setInterval(async () => {
      try {
        const [listResp, ...updates] = await Promise.all([
          hasPlanning ? api.get("/images").then((r) => r.data).catch(() => null) : Promise.resolve(null),
          ...ids.map((id) => api.get(`/images/${id}`).then((r) => r.data).catch(() => null)),
        ]);
        // 1) Replace the whole list if planning placeholders may have expanded.
        if (listResp && Array.isArray(listResp.images)) {
          setImages(listResp.images);
        }
        // 2) Merge per-id updates for existing generating rows.
        const fresh = updates.filter(Boolean);
        if (fresh.length && !listResp) {
          setImages((prev) => prev.map((img) => {
            const u = fresh.find((f) => f.id === img.id);
            return u ? { ...img, ...u } : img;
          }));
        }
        fresh.forEach((f) => {
          if (f.status === "ready") toast.success(lang === "ar" ? `تم إنشاء "${f.name}"` : `"${f.name}" is ready`);
          if (f.status === "failed") toast.error(lang === "ar" ? `فشل إنشاء "${f.name}"` : `Generation failed: "${f.name}"`);
        });
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatingIds, hasPlanning]);

  const handleUpload = async (file) => {
    if (!file) return;
    // Client-side validation
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast.error(lang === "ar" ? "نوع الملف غير مدعوم. الرجاء رفع JPG أو PNG أو WEBP." : "Unsupported file type. Please upload JPG, PNG or WEBP.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(lang === "ar" ? `حجم الملف يتجاوز ${MAX_MB} ميغابايت.` : `File exceeds ${MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/images/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      toast.success(lang === "ar" ? "تم رفع الصورة بنجاح" : "Image uploaded");
      setImages((prev) => [data, ...prev]);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message_ar || e?.response?.data?.detail?.message_en || (lang === "ar" ? "تعذّر رفع الصورة" : "Upload failed");
      toast.error(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (img) => {
    if (!window.confirm(lang === "ar" ? `هل تريد حذف "${img.name}"؟` : `Delete "${img.name}"?`)) return;
    try {
      await api.delete(`/images/${img.id}`);
      toast.success(lang === "ar" ? "تم حذف الصورة" : "Image deleted");
      setImages((prev) => prev.filter((x) => x.id !== img.id));
      if (detailsTarget?.id === img.id) setDetailsTarget(null);
    } catch {
      toast.error(lang === "ar" ? "تعذّر الحذف" : "Failed to delete");
    }
  };

  const handleRetry = async (img) => {
    try {
      await api.post(`/images/${img.id}/retry`);
      toast.success(lang === "ar" ? "أعادت دَعم المحاولة…" : "DAAM is retrying…");
      // Optimistic UI: flip local status back to generating so the card
      // immediately swaps to the shimmer loader instead of staying red.
      setImages((prev) => prev.map((x) =>
        x.id === img.id ? { ...x, status: "generating", error_message: null } : x
      ));
    } catch (e) {
      toast.error(lang === "ar" ? "تعذّرت إعادة المحاولة" : "Retry failed");
    }
  };

  const handleDownload = async (img) => {
    try {
      const token = getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/images/${img.id}/file?download=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const ext = (img.file_type || "image/png").split("/")[1] || "png";
      const safeName = `${(img.name || "image").replace(/[^\w\d._-]+/g, "-")}.${ext}`;

      // On mobile, use Web Share API when available so users can save to Photos
      // directly. Falls back to blob-download for desktop.
      const file = new File([blob], safeName, { type: blob.type || "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: img.name });
          return;
        } catch (shareErr) {
          if (shareErr && shareErr.name === "AbortError") return; // user cancelled
          // otherwise fall through to standard download
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = safeName;
      a.rel = "noopener";
      // Force iOS Safari to treat this as a download rather than a navigation.
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give Safari a beat before revoking, otherwise the download stalls.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error(lang === "ar" ? "تعذّر التحميل" : "Download failed");
    }
  };

  const handleRenameSaved = (updated) => {
    setImages((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setRenameTarget(null);
  };

  const handleDownloadSet = async (setId, setTitle) => {
    setDownloadingSet(setId);
    try {
      const token = getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/images/content-set/${setId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const safe = (setTitle || "content-set").replace(/[^\w\d\-. ]+/g, "-").slice(0, 60) || "content-set";
      const fileName = `${safe}.zip`;
      const file = new File([blob], fileName, { type: "application/zip" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: setTitle || "DAAM slides" });
          return;
        } catch (shareErr) {
          if (shareErr && shareErr.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.rel = "noopener";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error(lang === "ar" ? "تعذّر تحميل المجموعة" : "Failed to download set");
    } finally {
      setDownloadingSet(null);
    }
  };

  // Split images into slide-sets (content_set_id present, slide_total > 1)
  // and singles. Preserve the original order (newest first) by using the
  // creation position of the first slide seen for each set.
  const { slideSets, singles } = (() => {
    const setMap = new Map(); // setId -> { title, slides:[], firstIdx }
    const singlesArr = [];
    images.forEach((img, idx) => {
      const sid = img.content_set_id;
      if (sid && (img.slide_total || 0) > 1) {
        if (!setMap.has(sid)) {
          setMap.set(sid, {
            id: sid,
            title: img.set_title || img.name?.split("—")[0]?.trim() || (lang === "ar" ? "سلسلة شرائح" : "Slide series"),
            slides: [],
            firstIdx: idx,
          });
        }
        setMap.get(sid).slides.push(img);
      } else {
        singlesArr.push(img);
      }
    });
    const setsArr = Array.from(setMap.values()).map((s) => ({
      ...s,
      slides: s.slides.slice().sort((a, b) => (a.slide_index ?? 0) - (b.slide_index ?? 0)),
    }));
    setsArr.sort((a, b) => a.firstIdx - b.firstIdx);
    return { slideSets: setsArr, singles: singlesArr };
  })();

  const isEmpty = !loading && images.length === 0 && !q.trim() && filter === "all";
  const noResults = !loading && images.length === 0 && (q.trim() || filter !== "all");

  return (
    <AppShell>
      <div data-testid="images-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 data-testid="images-page-title" className="text-3xl md:text-4xl font-bold tracking-tight">
            {lang === "ar" ? "الصور" : "Images"}
          </h1>
          <p data-testid="images-page-subtitle" className="text-sm md:text-base text-muted-foreground mt-2 max-w-2xl">
            {lang === "ar"
              ? "اكتب موضوعاً — دَعم يختار الأسلوب البصري، يؤلّف المحتوى، ويسلّمك تصميماً جاهزاً للنشر"
              : "Type a topic — DAAM picks the visual style, writes the content, and delivers a ready-to-publish design"}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0 flex-wrap">
          <button
            data-testid="images-info-design-btn"
            onClick={() => setInfoModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm border border-primary/40 text-primary hover:bg-primary/10 text-sm font-medium transition-colors"
          >
            <FileText className="w-4 h-4" />
            {lang === "ar" ? "تصميم معلوماتي" : "Info Design"}
          </button>
          <button
            data-testid="images-ai-generate-btn"
            onClick={() => setAiModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            {lang === "ar" ? "إنشاء صورة بالذكاء الاصطناعي" : "Generate with AI"}
          </button>
          <button
            data-testid="images-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm border border-border/70 bg-card hover:bg-muted/60 text-sm font-medium disabled:opacity-60"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {lang === "ar" ? "رفع صورة" : "Upload"}
          </button>
          <input
            data-testid="images-file-input"
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div data-testid="images-upload-progress" className="border border-border/60 bg-card rounded-md p-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span>{lang === "ar" ? "جاري الرفع…" : "Uploading…"}</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 bg-muted/50 rounded-sm overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}

      {/* Filters + search */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`images-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${
                filter === f.key
                  ? "bg-primary/10 text-primary border-primary/40"
                  : "border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/60"
              }`}
            >
              {f.label[lang] || f.label.en}
            </button>
          ))}
        </div>
        <div className="relative flex-1 md:max-w-sm md:ms-auto">
          <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground pointer-events-none" />
          <input
            data-testid="images-search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "ar" ? "ابحث عن صورة بالاسم…" : "Search by name…"}
            className="w-full ps-9 pe-3 py-2 text-sm rounded-sm border border-border/60 bg-card focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* Grid / Empty / Loading */}
      {loading ? (
        <div data-testid="images-loading" className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : isEmpty ? (
        <div data-testid="images-empty-state" className="border border-dashed border-border/70 rounded-md py-16 px-6 text-center bg-muted/20">
          <div className="w-14 h-14 mx-auto rounded-sm bg-primary/10 flex items-center justify-center mb-4">
            <ImageIcon className="w-7 h-7 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-1">{lang === "ar" ? "لا توجد صور حتى الآن" : "No images yet"}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {lang === "ar" ? "ارفع أول صورة أو أنشئ صورة جديدة بالذكاء الاصطناعي." : "Upload your first image or generate one with AI."}
          </p>
          <div className="mt-5 flex justify-center gap-2 flex-wrap">
            <button data-testid="images-empty-upload-btn" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-border/70 bg-card hover:bg-muted/60 text-sm">
              <Upload className="w-4 h-4" /> {lang === "ar" ? "رفع صورة" : "Upload"}
            </button>
            <button data-testid="images-empty-ai-btn" onClick={() => setAiModal(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm">
              <Sparkles className="w-4 h-4" /> {lang === "ar" ? "إنشاء صورة" : "Generate image"}
            </button>
          </div>
        </div>
      ) : noResults ? (
        <div data-testid="images-no-results" className="text-center py-12 text-muted-foreground text-sm">
          {lang === "ar" ? "لا توجد صور مطابقة للبحث." : "No images match your search."}
        </div>
      ) : (
        <div>
          {slideSets.map((s) => (
            <SlideSetGroup
              key={s.id}
              setId={s.id}
              setTitle={s.title}
              slides={s.slides}
              lang={lang}
              downloading={downloadingSet === s.id}
              onDownloadAll={handleDownloadSet}
              onOpen={setDetailsTarget}
              onRename={setRenameTarget}
              onDelete={handleDelete}
              onDownload={handleDownload}
              onEdit={setEditTarget}
              onRetry={handleRetry}
            />
          ))}
          {singles.length > 0 && (
            <div data-testid="images-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {singles.map((img) => (
                <ImageCard
                  key={img.id}
                  img={img}
                  lang={lang}
                  onOpen={setDetailsTarget}
                  onRename={setRenameTarget}
                  onDelete={handleDelete}
                  onDownload={handleDownload}
                  onEdit={setEditTarget}
                  onRetry={handleRetry}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <RenameModal
        open={!!renameTarget}
        image={renameTarget}
        lang={lang}
        onClose={() => setRenameTarget(null)}
        onSaved={handleRenameSaved}
      />
      <DetailsModal
        open={!!detailsTarget}
        image={detailsTarget}
        lang={lang}
        onClose={() => setDetailsTarget(null)}
        onDelete={handleDelete}
        onDownload={handleDownload}
        onEdit={setEditTarget}
      />
      <AIGenerateModal
        open={aiModal}
        lang={lang}
        onClose={() => setAiModal(false)}
        onGenerated={(placeholder) => setImages((prev) => [placeholder, ...prev])}
      />
      <InfoDesignModal
        open={infoModal}
        lang={lang}
        onClose={() => setInfoModal(false)}
        onGenerated={(placeholder) => setImages((prev) => [placeholder, ...prev])}
      />
      <AIEditModal
        open={!!editTarget}
        image={editTarget}
        lang={lang}
        onClose={() => setEditTarget(null)}
        onEdited={(placeholder) => setImages((prev) => [placeholder, ...prev])}
      />
      </div>
    </AppShell>
  );
}
